import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import HighlightOffOutlinedIcon from '@material-ui/icons/HighlightOffOutlined';
import RadioButtonUncheckedIcon from '@material-ui/icons/RadioButtonUnchecked';
import Typography from '../Typography';
import { useTrans } from '../../../services/i18n';
import { getPasswordRequirementStatus } from '../../../utils/passwordPolicy';

const useStyles = makeStyles({
  root: {
    marginTop: 4,
  },
  label: {
    fontSize: 14,
    fontWeight: 600,
    color: '#657085',
    marginBottom: 8,
  },
  item: {
    display: 'flex',
    alignItems: 'center',
    padding: '3px 0',
  },
  icon: {
    fontSize: 18,
    marginRight: 10,
    flexShrink: 0,
  },
  iconMet: {
    color: '#17B471',
  },
  iconUnmet: {
    color: '#E2556A',
  },
  iconNeutral: {
    color: '#B2B8CF',
  },
  text: {
    fontSize: 14,
    fontWeight: 500,
    lineHeight: 1.4,
  },
  textMet: {
    color: '#17B471',
  },
  textUnmet: {
    color: '#E2556A',
  },
  textNeutral: {
    color: '#657085',
  },
});

const REQUIREMENTS = [
  { key: 'minLength', labelKey: 'password-requirement-min-length' },
  { key: 'lowercase', labelKey: 'password-requirement-lowercase' },
  { key: 'uppercase', labelKey: 'password-requirement-uppercase' },
  { key: 'number', labelKey: 'password-requirement-number' },
  { key: 'specialChar', labelKey: 'password-requirement-special' },
];

function RequirementItem({ met, failed, label, classes }) {
  const Icon = met ? CheckCircleOutlineIcon : failed ? HighlightOffOutlinedIcon : RadioButtonUncheckedIcon;
  const iconClass = met ? classes.iconMet : failed ? classes.iconUnmet : classes.iconNeutral;
  const textClass = met ? classes.textMet : failed ? classes.textUnmet : classes.textNeutral;

  return (
    <div className={classes.item}>
      <Icon className={`${classes.icon} ${iconClass}`} />
      <Typography component="span" className={`${classes.text} ${textClass}`} fontFamily="lexend">
        {label}
      </Typography>
    </div>
  );
}

export default function PasswordRequirements({ password = '', confirmPassword, showConfirmMatch = false }) {
  const { trans } = useTrans();
  const classes = useStyles();
  const status = getPasswordRequirementStatus(password);
  const hasPasswordInput = password.length > 0;
  const hasConfirmInput = confirmPassword?.length > 0;
  const passwordsMatch = hasConfirmInput && password === confirmPassword;

  return (
    <div className={classes.root}>
      <Typography className={classes.label} fontFamily="lexend">
        {trans('password-requirements-label')}
      </Typography>
      {REQUIREMENTS.map(({ key, labelKey }) => (
        <RequirementItem
          key={key}
          met={hasPasswordInput && status[key]}
          failed={hasPasswordInput && !status[key]}
          label={trans(labelKey)}
          classes={classes}
        />
      ))}
      {showConfirmMatch && (
        <RequirementItem
          met={passwordsMatch}
          failed={hasConfirmInput && !passwordsMatch}
          label={trans('password-requirement-match')}
          classes={classes}
        />
      )}
    </div>
  );
}
